import React, { useState, useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react'

interface OtpInputProps {
  length?: number
  onComplete: (otp: string) => void
  error?: string
  disabled?: boolean
}

export const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  onComplete,
  error,
  disabled = false
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const handleChange = (index: number, value: string): void => {
    if (disabled) return

    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return

    const newOtp = [...otp]

    // Handle multiple character paste
    if (value.length > 1) {
      const pastedData = value.slice(0, length).split('')
      pastedData.forEach((char, i) => {
        if (index + i < length) {
          newOtp[index + i] = char
        }
      })
      setOtp(newOtp)

      // Focus on the next empty input or last input
      const nextIndex = Math.min(index + pastedData.length, length - 1)
      inputRefs.current[nextIndex]?.focus()

      // Check if OTP is complete
      if (newOtp.every((digit) => digit !== '')) {
        onComplete(newOtp.join(''))
      }
    } else {
      // Single character input
      newOtp[index] = value
      setOtp(newOtp)

      // Move to next input if value is entered
      if (value && index < length - 1) {
        inputRefs.current[index + 1]?.focus()
      }

      // Check if OTP is complete
      if (newOtp.every((digit) => digit !== '')) {
        onComplete(newOtp.join(''))
      }
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
    if (disabled) return

    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        // If current input is empty, move to previous and clear it
        const newOtp = [...otp]
        newOtp[index - 1] = ''
        setOtp(newOtp)
        inputRefs.current[index - 1]?.focus()
      } else {
        // Clear current input
        const newOtp = [...otp]
        newOtp[index] = ''
        setOtp(newOtp)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>): void => {
    if (disabled) return

    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').slice(0, length)

    if (!/^\d+$/.test(pastedData)) return

    const newOtp = pastedData.split('')
    while (newOtp.length < length) {
      newOtp.push('')
    }
    setOtp(newOtp)

    // Focus on the last filled input
    const lastFilledIndex = Math.min(pastedData.length - 1, length - 1)
    inputRefs.current[lastFilledIndex]?.focus()

    // Check if OTP is complete
    if (newOtp.every((digit) => digit !== '')) {
      onComplete(newOtp.join(''))
    }
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex gap-2 justify-center">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`w-12 h-14 text-center text-2xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
              error
                ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white/5 text-whiteout'}`}
            aria-label={`Digit ${index + 1} of ${length}`}
          />
        ))}
      </div>
      {error && <p className="text-sm text-red-500 text-center">{error}</p>}
    </div>
  )
}
